
import { computed, defineComponent } from 'vue';
import { datetimeFormat } from '@/utils';
import DialogPurchaseCouponDetail from '@/components/organisms/coupon/DialogPurchaseCouponDetail.vue';
import { useCoupon } from '@/compositions/useCoupon';
import { Coupon } from '@/repositories/couponRepository';
import dayjs from 'dayjs';
import { i18n } from '@/plugins/i18n';
type Props = {
  coupon: Coupon;
};

export default defineComponent<Props>({
  name: 'PurchaseCoupon',

  props: {
    coupon: {
      type: Object,
      required: true,
    },
  },

  components: {
    DialogPurchaseCouponDetail,
  },

  setup(props: Props) {
    const { outOfStock } = useCoupon();
    const name = computed(() => props.coupon.couponMaster.name);

    const purchaseDisplayText = computed(() => {
      try {
        if (!props.coupon.purchaseCouponSettings) return null;
        const settings = JSON.parse(props.coupon.purchaseCouponSettings);
        return settings.purchaseCoupon.displayText || null;
      } catch {
        return null;
      }
    });

    // プレビュー画像の優先度 purchaseImageUrl > mainImageUrl
    const purchaseImageUrl = computed(() => {
      try {
        if (!props.coupon.purchaseCouponSettings) return null;
        const settings = JSON.parse(props.coupon.purchaseCouponSettings);
        return settings.purchaseCoupon.displayImageUrl || null;
      } catch {
        return null;
      }
    });
    const mainImageUrl = computed(() => {
      try {
        return JSON.parse(props.coupon.couponMaster.attrs).mainImageUrl || null;
      } catch {
        return null;
      }
    });

    const price = computed(() => {
      if (!props.coupon.purchaseCouponSettings) return null;
      const settings = JSON.parse(props.coupon.purchaseCouponSettings);
      return settings.purchaseCoupon.price;
    });

    /**
     * CS-6783 取得前クーポンの期限表示を管理画面へ寄せる
     */
    const expiresAt = computed(() => {
      const expires = JSON.parse(props.coupon.couponMaster.attrs).expires;
      // @note 0が来うるので != null判定
      if (expires.term != null) {
        return (
          `${i18n.t('keyword.coupon')}交換後 ${expires.term}日間` +
          (expires.termStartTime ? `<br/>（毎日${expires.termStartTime}から起算）` : '')
        );
      }
      if (expires.startAt && expires.endAt)
        return `${datetimeFormat(expires.startAt, 'date')} 〜 ${datetimeFormat(expires.endAt, 'date')}`;
      if (expires.startAt) return `${datetimeFormat(expires.startAt, 'date')} 〜`;
      if (expires.endAt) return `〜 ${datetimeFormat(expires.endAt, 'date')}`;
      return 'なし'; // 有効期限なし
    });

    const hasExpiresAt = computed(() => {
      const expires = JSON.parse(props.coupon.couponMaster.attrs).expires;
      if (expires.term != null && expires.termStartTime) return true;
      if (expires.startAt) return true;
      if (expires.endAt) return true;
      return false;
    });

    // TODO: want to use getRestrictionSettingLabel
    const restrictionSettings = computed(() => {
      if (!props.coupon.restrictionSettings) return null;
      return JSON.parse(props.coupon.restrictionSettings);
    });

    const restriction = computed(() => {
      const settings = restrictionSettings.value;
      if (!settings) return null;
      const { type, value } = settings.customer || {};

      switch (type) {
        case 'ONCE':
          return '１回だけ交換可能';
        case 'DAILY':
          return '毎日1回交換可能' + (value ? `\n(${value}更新)` : '');
        case 'TERM': {
          const termValue: string[] = [value || []].flat();
          return `期間中1回だけ交換可能 ${termValue.map(v => dayjs(v).format('YYYY/MM/DD HH:mm')).join('〜')}`;
        }
        default:
          return null;
      }
    });

    const purchaseCouponSettings = computed(() => {
      if (!props.coupon.purchaseCouponSettings) return null;
      return JSON.parse(props.coupon.purchaseCouponSettings);
    });

    const term = computed(() => {
      const settings = restrictionSettings.value;
      if (!settings) return null;
      const { type, value } = settings.customer || {};
      if (type !== 'TERM') return null;
      const termValue: string[] = [value || []].flat();
      return termValue.map(v => dayjs(v));
    });
    const hasTerm = computed(() => {
      return Boolean(term.value);
    });
    const withinTerm = computed(() => {
      if (!term.value) return false;
      const [from, to] = term.value;
      if (!from || !to) return false; // CS-4876
      const now = dayjs();
      return from.isBefore(now) && to.isAfter(now);
    });
    const disabled = computed(() => outOfStock(props.coupon));

    return {
      name,
      purchaseDisplayText,
      mainImageUrl,
      purchaseImageUrl,
      expiresAt,
      hasExpiresAt,
      restrictionSettings,
      purchaseCouponSettings,
      restriction,
      disabled,
      hasTerm,
      withinTerm,
      price,
    };
  },
});
